import { useEffect, useState } from 'react';
import { InvoiceProtocol, InvoiceProtocolPick, InvoiceProtocols } from '../models/table-models';
import { getInvoiceProtocols } from './clients-hooks';

/**
 * Отметките за протоколите по фактури - на едно място, защото ги искат два диалога: обединяването на
 * фактури и промяната на документ.
 *
 * Правилата, които трябва да са едни и същи на двете места:
 *
 * - минала ли е фактурата през избор (`protocolsPicked`), показва се **точно той** - иначе отметките са
 *   тези от периода, но само за обект, на който прикачването вече е включено;
 * - протокол, добавен с „+", се пази **по фактури**: той не идва от прозореца на фактурата и никъде
 *   другаде няма да го има при сглобяването на избора;
 * - изборът се праща и празен - значи „нищо да не тръгва с тази фактура" - но само за редове, чиито
 *   протоколи наистина са се заредили: пропаднала заявка не бива да се запише като изричен отказ.
 */
export const useProtocolPicks = (invoicingIds: number[], active: boolean) => {
    const [protocolsByInvoice, setProtocolsByInvoice] = useState<Map<number, InvoiceProtocols>>(new Map());
    const [selectedProtocolIds, setSelectedProtocolIds] = useState<Set<number>>(new Set());
    const [addedByInvoice, setAddedByInvoice] = useState<Map<number, InvoiceProtocol[]>>(new Map());
    // Докато тече заявката, клетката показва въртележка: „няма протоколи" изглежда като отговор.
    const [loading, setLoading] = useState(false);

    const key = invoicingIds.join(',');

    useEffect(() => {
        if (!active || invoicingIds.length === 0) return;

        setSelectedProtocolIds(new Set());
        setProtocolsByInvoice(new Map());
        setAddedByInvoice(new Map());

        let current = true;
        setLoading(true);
        getInvoiceProtocols(invoicingIds)
            .then((rows) => {
                if (!current) return;

                setProtocolsByInvoice(new Map(rows.map((row) => [row.invoicingId, row])));
                setSelectedProtocolIds(new Set(rows.flatMap((row) =>
                    row.protocolsPicked
                        ? row.pickedProtocolIds
                        : row.attachProtocols ? row.protocols.map((p) => p.id) : [])));
                // Избраният отвън периода трябва да се вижда на реда, а не само да е отметнат невидимо.
                setAddedByInvoice(new Map(rows
                    .filter((row) => row.protocolsPicked)
                    .map((row) => {
                        const periodIds = new Set(row.protocols.map((p) => p.id));
                        const outside = row.extraProtocols
                            .filter((p) => row.pickedProtocolIds.includes(p.id) && !periodIds.has(p.id));
                        return [row.invoicingId, outside] as [number, InvoiceProtocol[]];
                    })
                    .filter(([, outside]) => outside.length > 0)));
            })
            // Списъкът е удобство: без него диалогът пак работи.
            .catch(() => current && setProtocolsByInvoice(new Map()))
            .finally(() => current && setLoading(false));

        return () => {
            current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, key]);

    const toggleProtocol = (protocolId: number) =>
        setSelectedProtocolIds((prev) => {
            const next = new Set(prev);
            if (next.has(protocolId)) next.delete(protocolId);
            else next.add(protocolId);
            return next;
        });

    /** Протокол отвън: показва се на реда и тръгва отметнат - иначе добавянето му е без смисъл. */
    const addProtocol = (invoicingId: number, protocol: InvoiceProtocol) => {
        setAddedByInvoice((prev) => {
            const next = new Map(prev);
            const currentlyAdded = next.get(invoicingId) ?? [];
            if (!currentlyAdded.some(({ id }) => id === protocol.id)) {
                next.set(invoicingId, [...currentlyAdded, protocol]);
            }
            return next;
        });
        setSelectedProtocolIds((prev) => new Set(prev).add(protocol.id));
    };

    const addedFor = (invoicingId: number) => addedByInvoice.get(invoicingId) ?? [];

    const picksFor = (ids: number[]): InvoiceProtocolPick[] =>
        ids
            .map((id) => protocolsByInvoice.get(id))
            .filter((row): row is InvoiceProtocols => !!row)
            .map((row) => ({
                invoicingId: row.invoicingId,
                branchId: row.branchId,
                handoverIds: [...row.protocols, ...addedFor(row.invoicingId)]
                    .filter((p) => selectedProtocolIds.has(p.id))
                    .map((p) => p.id),
            }));

    return {
        protocolsByInvoice,
        loading,
        selectedProtocolIds,
        toggleProtocol,
        addProtocol,
        addedFor,
        picksFor,
    };
};
